import { useEffect, useRef, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import AddressAutocomplete from "./AddressAutocomplete";
import AuthImage from "./AuthImage";
import {
  Plus, Trash, FilePdf, PaperPlaneTilt, FileText, PencilSimple, Image as ImageIcon, Storefront, UploadSimple,
  CopySimple, FloppyDisk, BookOpen, MagicWand, Camera, EnvelopeSimple, Bell,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  submitted: "bg-blue-100 text-blue-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  converted: "bg-indigo-100 text-indigo-800",
};

const EMPTY_LINE = () => ({ id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2), description: "", quantity: 1, unit_cost: 0 });
const EMPTY_FORM = () => ({
  title: "",
  client_name: "",
  client_email: "",
  client_phone: "",
  client_company: "",
  street_address: "",
  town_city: "",
  county: "",
  postcode: "",
  property_address: "",
  latitude: null,
  longitude: null,
  validity_days: 30,
  vat_enabled: false,
  vat_rate: 20,
  markup_pct: 0,
  terms: "",
  notes: "",
  line_items: [EMPTY_LINE()],
});

function money(n) {
  return `£${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function totalsFor(form) {
  const lines = form.line_items || [];
  const base = lines.reduce((sum, l) => sum + ((Number(l.quantity) || 0) * (Number(l.unit_cost) || 0)), 0);
  const pct = Number(form.markup_pct) || 0;
  const markup = base * (pct / 100);
  const grand = base + markup;
  const vat = form.vat_enabled ? grand * ((Number(form.vat_rate) || 0) / 100) : 0;
  const r = (n) => Math.round(n * 100) / 100;
  return { base: r(base), markup: r(markup), grand: r(grand), vat: r(vat), total: r(grand + vat) };
}

export default function SupplierQuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [editing, setEditing] = useState(null);   // quote object or "new"
  const [form, setForm] = useState(EMPTY_FORM());
  const [saving, setSaving] = useState(false);

  // Branding state
  const [branding, setBranding] = useState({ company_name: "", tagline: "", logo_url: "", footer_text: "" });
  const [brandSaving, setBrandSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef(null);

  // Phase 228 — templates / send-email / save-as-template / line images / AI helper
  const [templates, setTemplates] = useState([]);
  const [showTplDialog, setShowTplDialog] = useState(false);
  const [showSaveTplDialog, setShowSaveTplDialog] = useState(false);
  const [saveTplName, setSaveTplName] = useState("");
  const [emailingQuote, setEmailingQuote] = useState(null);
  const [emailForm, setEmailForm] = useState({ to_email: "", subject: "", message: "", cc_emails: "", bcc_self: true, auto_followup: false });
  const [emailSending, setEmailSending] = useState(false);
  const lineImageRefs = useRef({}); // line-item id -> file input ref
  const [lineImageUploading, setLineImageUploading] = useState(false);
  const [aiDraftingIdx, setAiDraftingIdx] = useState(null);
  const [followupPrefs, setFollowupPrefs] = useState({ auto_enabled: false, intervals_days: [3, 7, 14], max_chases: 3 });

  // Markup presets — private per supplier
  const [markupTemplates, setMarkupTemplates] = useState([]);
  const [showPresetSave, setShowPresetSave] = useState(false);
  const [presetName, setPresetName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [q, b, tpl, fp, mt] = await Promise.all([
        API.get("/supplier-portal/quotes"),
        API.get("/supplier-portal/quote-branding"),
        API.get("/supplier-portal/quote-templates").catch(() => ({ data: { templates: [] } })),
        API.get("/supplier-portal/quote-followup-prefs").catch(() => ({ data: { prefs: null } })),
        API.get("/supplier-portal/markup-templates").catch(() => ({ data: { templates: [] } })),
      ]);
      setQuotes(q.data.quotes || []);
      setBranding(b.data.branding || { company_name: "", tagline: "", logo_url: "", footer_text: "" });
      setTemplates(tpl.data.templates || []);
      setMarkupTemplates(mt.data.templates || []);
      if (fp.data.prefs) setFollowupPrefs(fp.data.prefs);
    } catch {
      toast.error("Couldn't load your quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM()); setEditing("new"); };

  const openEdit = async (id) => {
    try {
      const { data } = await API.get(`/supplier-portal/quotes/${id}`);
      setForm({
        ...EMPTY_FORM(),
        ...data,
        markup_pct: data.markup_a_pct || 0,
        line_items: (data.line_items && data.line_items.length) ? data.line_items : [EMPTY_LINE()],
      });
      setEditing(data);
    } catch {
      toast.error("Couldn't load that quote");
    }
  };

  const close = () => { setEditing(null); setForm(EMPTY_FORM()); };

  const updateLine = (i, patch) => {
    setForm(f => ({ ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  };

  const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, EMPTY_LINE()] }));
  const removeLine = (i) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const save = async (andSubmit = false) => {
    if (!form.title?.trim()) { toast.error("Title is required"); return; }
    if (!(form.line_items || []).length) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      let id = editing?.id;
      const payload = { ...form, vat_rate: Number(form.vat_rate) || 0, validity_days: Number(form.validity_days) || 30, markup_pct: Number(form.markup_pct) || 0 };
      if (editing === "new") {
        const { data } = await API.post("/supplier-portal/quotes", payload);
        id = data.id;
      } else {
        await API.put(`/supplier-portal/quotes/${id}`, payload);
      }
      if (andSubmit) {
        await API.post(`/supplier-portal/quotes/${id}/submit`);
        toast.success("Quote submitted!");
      } else {
        toast.success(editing === "new" ? "Draft saved" : "Quote updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this draft quote?")) return;
    try {
      await API.delete(`/supplier-portal/quotes/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await API.post(`/supplier-portal/quotes/${q.id}/pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${q.reference}_supplier.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to generate PDF");
    }
  };

  const duplicateQuote = async (q) => {
    try {
      const { data } = await API.post(`/supplier-portal/quotes/${q.id}/duplicate`);
      toast.success(`Duplicated as ${data.reference}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to duplicate"); }
  };

  const openEmailDialog = (q) => {
    setEmailForm({
      to_email: q.client_email || "",
      subject: `Your quote ${q.reference} from ${branding.company_name || "us"}`,
      message: `Hi ${q.client_name || "there"},\n\nPlease find attached our quote ${q.reference} for your review. Let me know if you have any questions.\n\nThanks,\n${branding.company_name || ""}`,
      cc_emails: "",
      bcc_self: true,
      auto_followup: !!followupPrefs.auto_enabled,
    });
    setEmailingQuote(q);
  };
  const sendEmail = async () => {
    if (!emailingQuote) return;
    if (!emailForm.to_email || !emailForm.to_email.includes("@")) { toast.error("Enter a valid recipient email"); return; }
    setEmailSending(true);
    try {
      await API.post(`/supplier-portal/quotes/${emailingQuote.id}/send-email`, emailForm);
      toast.success(`Quote emailed to ${emailForm.to_email}`);
      setEmailingQuote(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally { setEmailSending(false); }
  };

  const applyTemplate = (tpl) => {
    setForm(f => ({
      ...f,
      line_items: (tpl.line_items && tpl.line_items.length)
        ? tpl.line_items.map(li => ({ ...EMPTY_LINE(), ...li, id: undefined }))
        : f.line_items,
      terms: tpl.terms || f.terms,
      validity_days: tpl.validity_days || f.validity_days,
      vat_enabled: typeof tpl.vat_enabled === "boolean" ? tpl.vat_enabled : f.vat_enabled,
      vat_rate: tpl.vat_rate || f.vat_rate,
      markup_pct: tpl.markup_pct !== undefined ? tpl.markup_pct : f.markup_pct,
    }));
    toast.success(`Loaded template: ${tpl.name}`);
    setShowTplDialog(false);
  };
  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await API.delete(`/supplier-portal/quote-templates/${tpl.id}`);
      toast.success("Template deleted");
      const { data } = await API.get("/supplier-portal/quote-templates");
      setTemplates(data.templates || []);
    } catch { toast.error("Failed to delete template"); }
  };
  const saveAsTemplate = async () => {
    if (!saveTplName.trim()) { toast.error("Name required"); return; }
    if (!editing || editing === "new") { toast.error("Save the quote first"); return; }
    try {
      await API.post(`/supplier-portal/quotes/${editing.id}/save-as-template`, { name: saveTplName.trim() });
      toast.success(`Saved as template`);
      setShowSaveTplDialog(false);
      setSaveTplName("");
      const { data } = await API.get("/supplier-portal/quote-templates");
      setTemplates(data.templates || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save template"); }
  };

  const aiDraftLineDescription = async (idx) => {
    const line = form.line_items[idx];
    const prompt = (line?.description || "").trim();
    if (!prompt) { toast.error("Type a few keywords first, then click ✨"); return; }
    setAiDraftingIdx(idx);
    try {
      const { data } = await API.post("/ai/vendor/draft-line-description", { prompt });
      if (data.description) updateLine(idx, { description: data.description });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI helper unavailable");
    } finally { setAiDraftingIdx(null); }
  };

  const uploadLineImage = async (file, lineId) => {
    if (!file) return;
    if (!editing || editing === "new") { toast.error("Save the draft once before adding images"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setLineImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (lineId) fd.append("line_item_id", lineId);
      const { data } = await API.post(`/supplier-portal/quotes/${editing.id}/line-images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({ ...f, images: [...(f.images || []), data.image] }));
      toast.success("Image added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setLineImageUploading(false); }
  };
  const removeLineImage = async (image_id) => {
    if (!editing || editing === "new") return;
    try {
      await API.delete(`/supplier-portal/quotes/${editing.id}/line-images/${image_id}`);
      setForm(f => ({ ...f, images: (f.images || []).filter(i => i.id !== image_id) }));
    } catch { toast.error("Failed to remove image"); }
  };

  const saveFollowupPrefs = async (next) => {
    setFollowupPrefs(next);
    try { await API.put("/supplier-portal/quote-followup-prefs", { prefs: next }); }
    catch { toast.error("Failed to save preferences"); }
  };

  const saveMarkupPreset = async () => {
    if (!presetName.trim()) { toast.error("Give the preset a name"); return; }
    try {
      const { data } = await API.post("/supplier-portal/markup-templates", { name: presetName.trim(), markup_pct: Number(form.markup_pct) || 0 });
      setMarkupTemplates(prev => [...prev, data.template].sort((a, b) => a.name.localeCompare(b.name)));
      setPresetName("");
      setShowPresetSave(false);
      toast.success(data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save preset"); }
  };
  const deleteMarkupPreset = async (tpl) => {
    try {
      await API.delete(`/supplier-portal/markup-templates/${tpl.id}`);
      setMarkupTemplates(prev => prev.filter(t => t.id !== tpl.id));
    } catch { toast.error("Failed to delete preset"); }
  };

  const saveBranding = async () => {
    setBrandSaving(true);
    try {
      const { data } = await API.put("/supplier-portal/quote-branding", { branding });
      setBranding(data.branding || branding);
      toast.success("Branding saved");
    } catch {
      toast.error("Failed to save branding");
    } finally {
      setBrandSaving(false);
    }
  };

  const uploadLogoFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/supplier-portal/quote-branding/logo-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBranding(b => ({ ...b, ...(data.branding || {}), logo_url: data.url || (data.branding && data.branding.logo_url) || b.logo_url }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to upload logo");
    } finally {
      setLogoUploading(false);
      if (logoFileRef.current) logoFileRef.current.value = "";
    }
  };

  const totals = totalsFor(form);

  return (
    <div className="space-y-6" data-testid="supplier-quotes-tab">
      {/* Header + new button */}
      <div className="flex items-center justify-between">
        <h2 className="text-base sm:text-lg font-semibold">Your quotes</h2>
        <Button size="sm" onClick={openNew} data-testid="supplier-new-quote-btn">
          <Plus size={14} className="mr-1.5" /> New quote
        </Button>
      </div>

      {/* Quote list */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : quotes.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
          You haven't created any quotes yet. Click "New quote" to send your first.
        </div>
      ) : (
        <div className="space-y-2">
          {quotes.map(q => (
            <div key={q.id} className="p-3 rounded-lg border border-border bg-card hover:border-primary/60 transition" data-testid={`supplier-quote-row-${q.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                    <h3 className="font-semibold truncate">{q.title}</h3>
                    <Badge className={`text-[9px] uppercase ${STATUS_TONE[q.status] || ""}`}>{q.status}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{q.reference} · {q.client_name || "No client set"} · {money(q.total_inc_vat || q.grand_total)}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {q.status === "draft" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(q.id)} data-testid={`edit-quote-${q.id}`}><PencilSimple size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(q.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-quote-${q.id}`}><Trash size={14} /></Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => duplicateQuote(q)} title="Duplicate" data-testid={`duplicate-quote-${q.id}`}><CopySimple size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEmailDialog(q)} title="Email quote" data-testid={`email-quote-${q.id}`}><PaperPlaneTilt size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(q)} data-testid={`pdf-quote-${q.id}`}><FilePdf size={14} className="mr-1" /> PDF</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Branding card */}
      <div className="border border-border rounded-md p-4" data-testid="supplier-quote-branding-card">
        <div className="flex items-center gap-2 mb-3">
          <Storefront size={16} className="text-primary" />
          <h3 className="text-sm font-semibold">Branding for your quotes</h3>
          <Badge variant="secondary" className="text-[10px]">Optional</Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Business name</Label>
            <Input value={branding.company_name || ""} onChange={e => setBranding(b => ({ ...b, company_name: e.target.value }))}
              placeholder="Sparks Electric Ltd" data-testid="branding-company" />
          </div>
          <div>
            <Label className="text-xs">Tagline</Label>
            <Input value={branding.tagline || ""} onChange={e => setBranding(b => ({ ...b, tagline: e.target.value }))}
              placeholder="Trusted electricians since 2008" data-testid="branding-tagline" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Logo</Label>
            <div className="flex items-start gap-3 mt-1">
              {branding.logo_url ? (
                <div className="flex-shrink-0 h-16 w-16 rounded-md border border-border bg-muted/30 overflow-hidden flex items-center justify-center" data-testid="branding-logo-preview">
                  <img src={branding.logo_url} alt="Logo preview" className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
              ) : (
                <div className="flex-shrink-0 h-16 w-16 rounded-md border border-dashed border-border bg-muted/20 flex items-center justify-center text-muted-foreground" data-testid="branding-logo-empty">
                  <ImageIcon size={20} />
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    ref={logoFileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={(e) => uploadLogoFile(e.target.files?.[0])}
                    data-testid="branding-logo-file-input"
                  />
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => logoFileRef.current?.click()}
                    disabled={logoUploading}
                    data-testid="branding-logo-upload-btn">
                    <UploadSimple size={14} className="mr-1.5" />
                    {logoUploading ? "Uploading…" : (branding.logo_url ? "Replace" : "Upload logo")}
                  </Button>
                  {branding.logo_url && (
                    <Button type="button" size="sm" variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setBranding(b => ({ ...b, logo_url: "" }))}
                      data-testid="branding-logo-remove-btn">
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
                <Input
                  value={branding.logo_url || ""}
                  onChange={e => setBranding(b => ({ ...b, logo_url: e.target.value }))}
                  placeholder="…or paste a logo URL: https://your-domain.com/logo.png"
                  data-testid="branding-logo"
                />
                <p className="text-[10px] text-muted-foreground">Upload a PNG/JPG/SVG/WebP (max 5MB) or paste a public URL.</p>
              </div>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Footer text</Label>
            <Textarea value={branding.footer_text || ""} onChange={e => setBranding(b => ({ ...b, footer_text: e.target.value }))}
              placeholder="VAT 123 4567 89 · Insurance to £5m · Reg 12345678" rows={2} data-testid="branding-footer" />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button size="sm" onClick={saveBranding} disabled={brandSaving} data-testid="save-branding-btn">
            {brandSaving ? "Saving…" : "Save branding"}
          </Button>
        </div>
      </div>

      {/* Auto follow-up preferences */}
      <div className="border border-border rounded-md p-4" data-testid="supplier-followup-card">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={16} className="text-primary" />
          <h3 className="text-sm font-semibold">Auto-chase your clients</h3>
          <Badge variant="secondary" className="text-[10px]">Smart</Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-3">When you email a quote with "Auto-follow-up" ticked, we'll send a polite chase reminder if the client hasn't replied. Perfect for busy days.</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              checked={!!followupPrefs.auto_enabled}
              onCheckedChange={(v) => saveFollowupPrefs({ ...followupPrefs, auto_enabled: v })}
              data-testid="followup-auto-enabled"
            />
            <Label className="text-sm cursor-pointer">Default to "Auto-follow-up" on every send</Label>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-muted-foreground">Chase after</span>
            <Input type="text" className="w-24 h-7 text-xs"
              value={(followupPrefs.intervals_days || []).join(",")}
              onChange={e => {
                const arr = e.target.value.split(",").map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
                saveFollowupPrefs({ ...followupPrefs, intervals_days: arr.length ? arr : [3, 7, 14] });
              }}
              data-testid="followup-intervals" />
            <span className="text-muted-foreground">days</span>
          </div>
        </div>
      </div>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editing === "new" ? "New supplier quote" : `Edit ${editing?.reference || "quote"}`}</DialogTitle>
            <DialogDescription>Quotes you create here are sent straight to Fixlr as a supplier quote.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required data-testid="quote-title" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Client / contact name</Label><Input value={form.client_name || ""} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} data-testid="quote-client-name" /></div>
              <div><Label>Client company</Label><Input value={form.client_company || ""} onChange={e => setForm(f => ({ ...f, client_company: e.target.value }))} /></div>
              <div><Label>Client email</Label><Input type="email" value={form.client_email || ""} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} /></div>
              <div><Label>Client phone</Label><Input value={form.client_phone || ""} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} /></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>Property address</Label>
                <AddressAutocomplete
                  value={form.property_address || form.street_address || ""}
                  onChange={v => setForm(f => ({ ...f, property_address: v, street_address: v }))}
                  onSelect={(a) => setForm(f => ({
                    ...f,
                    property_address: a.formatted_address || f.property_address,
                    street_address: a.street_address || "",
                    town_city: a.town_city || "",
                    county: a.county || "",
                    postcode: a.postcode || "",
                    latitude: a.latitude || null,
                    longitude: a.longitude || null,
                  }))}
                  data-testid="supplier-quote-address-input"
                />
              </div>
              <div><Label>Postcode</Label><Input value={form.postcode || ""} onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))} data-testid="supplier-quote-postcode" /></div>
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line items</Label>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setShowTplDialog(true)} data-testid="quote-load-template" title="Load template"><BookOpen size={12} className="mr-1" /> Templates</Button>
                  {editing && editing !== "new" && (
                    <Button size="sm" variant="outline" onClick={() => { setSaveTplName(form.title || ""); setShowSaveTplDialog(true); }} data-testid="quote-save-as-template" title="Save as template"><FloppyDisk size={12} className="mr-1" /> Save tpl</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={addLine} data-testid="quote-add-line"><Plus size={12} className="mr-1" /> Add line</Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.line_items.map((line, i) => {
                  const lineTotal = (Number(line.quantity) || 0) * (Number(line.unit_cost) || 0);
                  const lineId = line.id || "";
                  const lineImages = (form.images || []).filter(im => im.line_item_id === lineId);
                  return (
                    <div key={i} className="space-y-1.5 pb-2 border-b border-border/30 last:border-0" data-testid={`quote-line-${i}`}>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 sm:col-span-6">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] uppercase">Description</Label>
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-violet-600 hover:text-violet-700"
                              onClick={() => aiDraftLineDescription(i)}
                              disabled={aiDraftingIdx === i}
                              data-testid={`quote-ai-draft-${i}`}
                              title="AI: polish this description">
                              <MagicWand size={12} className="mr-0.5" /> {aiDraftingIdx === i ? "…" : "AI"}
                            </Button>
                          </div>
                          <Input value={line.description || ""} onChange={e => updateLine(i, { description: e.target.value })} />
                        </div>
                        <div className="col-span-3 sm:col-span-2"><Label className="text-[10px] uppercase">Qty</Label><Input type="number" min="0" step="0.01" value={line.quantity} onChange={e => updateLine(i, { quantity: e.target.value })} /></div>
                        <div className="col-span-5 sm:col-span-2"><Label className="text-[10px] uppercase">Unit £</Label><Input type="number" min="0" step="0.01" value={line.unit_cost} onChange={e => updateLine(i, { unit_cost: e.target.value })} /></div>
                        <div className="col-span-3 sm:col-span-1 text-right text-xs font-semibold pb-2">{money(lineTotal)}</div>
                        <div className="col-span-1 pb-1">
                          {form.line_items.length > 1 && (
                            <Button size="sm" variant="ghost" onClick={() => removeLine(i)} className="text-red-600" data-testid={`quote-remove-line-${i}`}><Trash size={12} /></Button>
                          )}
                        </div>
                      </div>
                      {/* Line images (only on saved drafts) */}
                      {editing && editing !== "new" && (
                        <div className="flex flex-wrap items-center gap-1.5 pl-1">
                          {lineImages.map(im => (
                            <div key={im.id} className="relative group h-12 w-12 rounded border border-border overflow-hidden bg-muted/20" data-testid={`line-image-${im.id}`}>
                              <AuthImage path={im.url} alt="" className="h-full w-full object-cover" />
                              <button type="button" onClick={() => removeLineImage(im.id)} className="absolute top-0 right-0 bg-red-600 text-white rounded-bl px-1 opacity-0 group-hover:opacity-100 transition" title="Remove">
                                <Trash size={10} />
                              </button>
                            </div>
                          ))}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={el => { if (el) lineImageRefs.current[i] = el; }}
                            onChange={(e) => { uploadLineImage(e.target.files?.[0], lineId); e.target.value = ""; }}
                            data-testid={`line-image-input-${i}`}
                          />
                          <Button size="sm" variant="outline" className="h-7 text-[10px]"
                            onClick={() => lineImageRefs.current[i]?.click()}
                            disabled={lineImageUploading}
                            data-testid={`line-image-upload-${i}`}>
                            <Camera size={11} className="mr-1" /> {lineImages.length ? "Add another" : "Add photo"}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Markup — supplier's own private uplift */}
            <div className="border border-border rounded-md p-3 bg-muted/20" data-testid="quote-markup-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <Label className="text-sm">Your markup</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Applied on top of your cost prices. Kept private — only your final prices are visible on the quote.</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input type="number" min="0" max="500" step="0.5" value={form.markup_pct} onChange={e => setForm(f => ({ ...f, markup_pct: e.target.value }))} className="w-20 h-8" data-testid="quote-markup-pct" />
                  <span className="text-xs">%</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                {markupTemplates.map(t => (
                  <span key={t.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] cursor-pointer ${Number(form.markup_pct) === Number(t.markup_pct) ? "border-primary bg-primary/10 text-primary" : "border-border bg-background hover:border-primary/50"}`}
                    onClick={() => { setForm(f => ({ ...f, markup_pct: t.markup_pct })); }}
                    data-testid={`markup-preset-${t.id}`}>
                    {t.name} · {t.markup_pct}%
                    <button type="button" className="text-muted-foreground hover:text-destructive ml-0.5" onClick={(e) => { e.stopPropagation(); deleteMarkupPreset(t); }} title="Delete preset" data-testid={`markup-preset-delete-${t.id}`}>×</button>
                  </span>
                ))}
                {showPresetSave ? (
                  <span className="inline-flex items-center gap-1">
                    <Input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="Preset name" className="h-7 w-32 text-xs" data-testid="markup-preset-name" />
                    <Button size="sm" className="h-7 text-[10px]" onClick={saveMarkupPreset} data-testid="markup-preset-save">Save</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => { setShowPresetSave(false); setPresetName(""); }}>Cancel</Button>
                  </span>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setShowPresetSave(true)} data-testid="markup-preset-add">
                    <FloppyDisk size={11} className="mr-1" /> Save {Number(form.markup_pct) || 0}% as preset
                  </Button>
                )}
              </div>
            </div>

            {/* VAT + totals */}
            <div className="border border-border rounded-md p-3 bg-muted/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Switch checked={!!form.vat_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, vat_enabled: v }))} data-testid="quote-vat-toggle" />
                  <Label className="text-sm cursor-pointer">Charge VAT</Label>
                </div>
                {form.vat_enabled && (
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min="0" max="100" step="0.5" value={form.vat_rate} onChange={e => setForm(f => ({ ...f, vat_rate: e.target.value }))} className="w-20 h-8" data-testid="quote-vat-rate" />
                    <span className="text-xs">%</span>
                  </div>
                )}
              </div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Your cost subtotal</span><span>{money(totals.base)}</span></div>
                {Number(form.markup_pct) > 0 && (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">Markup ({Number(form.markup_pct)}%)</span><span data-testid="quote-markup-amount">+{money(totals.markup)}</span></div>
                    <div className="flex justify-between font-medium"><span className="text-muted-foreground">Quoted subtotal</span><span data-testid="quote-subtotal-after-markup">{money(totals.grand)}</span></div>
                  </>
                )}
                {form.vat_enabled && <div className="flex justify-between"><span className="text-muted-foreground">VAT</span><span>{money(totals.vat)}</span></div>}
                <div className="flex justify-between font-bold pt-1 border-t border-border"><span>Total</span><span data-testid="quote-total">{money(totals.total)}</span></div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Validity (days)</Label><Input type="number" min="1" value={form.validity_days} onChange={e => setForm(f => ({ ...f, validity_days: e.target.value }))} /></div>
            </div>

            <div><Label>Terms</Label><Textarea rows={2} value={form.terms || ""} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} placeholder="e.g. 50% deposit, balance on completion" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">
              {saving ? "Saving…" : "Save draft"}
            </Button>
            <Button onClick={() => save(true)} disabled={saving} data-testid="submit-quote-btn">
              <PaperPlaneTilt size={14} className="mr-1.5" /> {saving ? "Submitting…" : "Save & submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Templates picker ── */}
      <Dialog open={showTplDialog} onOpenChange={setShowTplDialog}>
        <DialogContent className="max-w-md" data-testid="quote-templates-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Your quote templates</DialogTitle>
            <DialogDescription>Reusable line-item sets. Saves you re-typing the same job over and over.</DialogDescription>
          </DialogHeader>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No templates yet. Save a quote as a template from the editor to reuse it.</p>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {templates.map(t => (
                <div key={t.id} className="flex items-center justify-between p-2.5 rounded-md border border-border hover:border-primary/50 transition" data-testid={`template-row-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{t.name}</p>
                    <p className="text-[10px] text-muted-foreground">{(t.line_items || []).length} lines</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" onClick={() => applyTemplate(t)} data-testid={`use-template-${t.id}`}>Use</Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteTemplate(t)} className="text-red-600"><Trash size={12} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Save-as-template ── */}
      <Dialog open={showSaveTplDialog} onOpenChange={setShowSaveTplDialog}>
        <DialogContent className="max-w-sm" data-testid="quote-save-tpl-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Save as template</DialogTitle>
            <DialogDescription>Capture this quote's lines + terms for re-use later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Template name</Label>
            <Input value={saveTplName} onChange={e => setSaveTplName(e.target.value)} placeholder="e.g. Standard 2-bed boiler service" autoFocus data-testid="save-template-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTplDialog(false)}>Cancel</Button>
            <Button onClick={saveAsTemplate} data-testid="save-template-confirm">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Email quote ── */}
      <Dialog open={!!emailingQuote} onOpenChange={(o) => { if (!o) setEmailingQuote(null); }}>
        <DialogContent className="max-w-lg" data-testid="quote-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Email this quote</DialogTitle>
            <DialogDescription>Sends a branded PDF to the recipient with a personal message. Submitted quotes can be re-sent any time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>To</Label>
              <Input type="email" value={emailForm.to_email} onChange={e => setEmailForm(f => ({ ...f, to_email: e.target.value }))} required data-testid="quote-email-to" />
            </div>
            <div>
              <Label>CC (comma separated)</Label>
              <Input value={emailForm.cc_emails} onChange={e => setEmailForm(f => ({ ...f, cc_emails: e.target.value }))} placeholder="optional" data-testid="quote-email-cc" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} data-testid="quote-email-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={5} value={emailForm.message} onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))} data-testid="quote-email-message" />
            </div>
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={emailForm.bcc_self} onChange={e => setEmailForm(f => ({ ...f, bcc_self: e.target.checked }))} />
                Send me a copy
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer" title="Auto-chase if no reply">
                <input type="checkbox" checked={emailForm.auto_followup} onChange={e => setEmailForm(f => ({ ...f, auto_followup: e.target.checked }))} data-testid="quote-email-auto-followup" />
                Auto-follow-up <Bell size={11} />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailingQuote(null)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending} data-testid="quote-email-send"><EnvelopeSimple size={14} className="mr-1" /> {emailSending ? "Sending…" : "Send"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Suppress unused-import warning when the icon is referenced in JSX later
export const _icons = { ImageIcon };
